import { toast } from "sonner";

/** Document types shown in modal but with no print route yet. */
export const COMING_SOON_EXPORT_PRINT_IDS = new Set([]);

/** Security Endorsement + Screening Consent — Limco (companyId 5) only */
const LIMCO_ONLY_PRINT_COMPANY_ID = 5;
const LIMCO_ONLY_PRINT_IDS = new Set([
  "security_endorsement",
  "screening_consent",
]);

const resolveExportPrintCompanyId = (explicitCompanyId) => {
  if (explicitCompanyId != null && explicitCompanyId !== "") {
    return Number(explicitCompanyId);
  }
  try {
    const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
    return Number(authUser?.companyId);
  } catch {
    return NaN;
  }
};

const isLimcoOnlyPrintVisible = (companyId) =>
  Number(companyId) === LIMCO_ONLY_PRINT_COMPANY_ID;

export const appendBlLetterhead = (url, blPrintOnLetterhead) => {
  if (!blPrintOnLetterhead) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}blLetterhead=1`;
};

const buildHblPrintUrl = (bookingId, fileNumber, params = {}) => {
  const qs = new URLSearchParams();
  qs.set("bookingId", String(bookingId));
  if (fileNumber) qs.set("fileNumber", fileNumber);
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === false || value === "") return;
    if (value === true) qs.set(key, "1");
    else qs.set(key, String(value));
  });
  return `/hblPrint?${qs.toString()}`;
};

const buildDocPrintUrl = (path, bookingId, fileNumber) => {
  const qs = new URLSearchParams();
  qs.set("bookingId", String(bookingId));
  if (fileNumber) qs.set("fileNumber", fileNumber);
  return `${path}?${qs.toString()}`;
};

/**
 * Full export print list — matches APL Print Documents modal order.
 * @param {{ bookingId?: number|string, fileNumber?: string, defaultSelectedIds?: string|string[], companyId?: number|string }} ctx
 * @returns {import('@/components/PrintDocuments/PrintDocumentsSelectionModal').PrintDocumentItem[]}
 */
export function buildExportShipmentPrintDocuments({
  bookingId,
  fileNumber,
  defaultSelectedIds,
  companyId: companyIdArg,
} = {}) {
  if (!bookingId || Number(bookingId) <= 0) return [];

  const fileRef = fileNumber || "—";
  const selectedSet =
    defaultSelectedIds == null
      ? null
      : new Set(
          Array.isArray(defaultSelectedIds)
            ? defaultSelectedIds
            : [defaultSelectedIds],
        );
  const companyId = resolveExportPrintCompanyId(companyIdArg);
  const showLimcoOnlyPrints = isLimcoOnlyPrintVisible(companyId);

  const docs = [
    {
      id: "booking_confirmation_freighted",
      name: "BOOKING CONFIRMATION",
      remarks: `File: ${fileRef}`,
      defaultSelected: true,
    },
    {
      id: "dock_receipt",
      name: "DOCK RECEIPT",
      remarks: `Dock Receipt — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "pre_alert",
      name: "PRE ALERT",
      remarks: `Pre Alert — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "trucker_exporter_instructions",
      name: "TRUCKER EXPORTER INSTRUCTIONS",
      remarks: `Work Order — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "mbl_master",
      name: "BILL OF LADING MASTER",
      remarks: `MBL — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_original_freighted",
      name: "BILL OF LADING ORIGINAL FREIGHTED",
      remarks: `Original watermark, with charges — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_original_non_freighted",
      name: "BILL OF LADING ORIGINAL NON - FREIGHTED",
      remarks: `Original watermark, no charges — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_non_nego_freighted",
      name: "BILL OF LADING NON NEGO. (FREIGHTED)",
      remarks: `Non-negotiable watermark, with charges — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_non_nego_non_freighted",
      name: "BILL OF LADING NON NEGO. (NON - FREIGHTED)",
      remarks: `Non-negotiable watermark, no charges — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "waybill_non_nego",
      name: "WAYBILL NON NEGO",
      remarks: `Waybill non-negotiable watermark — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_draft_proof",
      name: "BILL OF LADING DRAFT PROOF COPY",
      remarks: `Draft watermark — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_freighted_no_sign_watermark",
      name: "BILL OF LADING - FREIGHTED (NO SIGN. & WATER MARK)",
      remarks: `With charges, no stamp/watermark — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "hbl_non_freighted_no_sign_watermark",
      name: "BILL OF LADING - NON FREIGHTED (NO SIGN. & WATER MARK)",
      remarks: `No charges, no stamp/watermark — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "waybill",
      name: "WAYBILL",
      remarks: `Waybill watermark — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "certificate_of_origin",
      name: "CERTIFICATE OF ORIGIN",
      remarks: `Certificate of Origin — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "commercial_invoice",
      name: "COMMERCIAL INVOICE",
      remarks: `Commercial Invoice — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "packing_list",
      name: "PACKING LIST",
      remarks: `Packing List — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "booking_confirmation_non_freighted",
      name: "BOOKING NON - FREIGHTED",
      remarks: `File: ${fileRef} — charges omitted`,
      defaultSelected: false,
    },
    {
      id: "cover_page",
      name: "COVER PAGE",
      remarks: `Cover Page — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "fcr",
      name: "FCR",
      remarks: `Forwarders Certificate of Receipt — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "istruzioni_sdoganamento",
      name: "ISTRUZIONI DI SDOGANAMENTO",
      remarks: `Customs clearance instructions — File: ${fileRef}`,
      defaultSelected: false,
    },
    {
      id: "ordine_di_trasporto",
      name: "ORDINE DI TRASPORTO",
      remarks: `Transport order — File: ${fileRef}`,
      defaultSelected: false,
    },
    ...(showLimcoOnlyPrints
      ? [
          {
            id: "security_endorsement",
            name: "SECURITY ENDORSEMENT",
            remarks: `Security Endorsement — File: ${fileRef}`,
            defaultSelected: false,
          },
          {
            id: "screening_consent",
            name: "SCREENING CONSENT",
            remarks: `Screening Consent — File: ${fileRef}`,
            defaultSelected: false,
          },
        ]
      : []),
  ];

  if (!selectedSet) return docs;

  return docs.map((doc) => ({
    ...doc,
    defaultSelected: selectedSet.has(doc.id),
  }));
}

/**
 * @returns {string|null}
 */
export function resolveExportPrintPath(itemId, { bookingId, fileNumber, companyId } = {}) {
  if (!bookingId || Number(bookingId) <= 0) return null;
  if (COMING_SOON_EXPORT_PRINT_IDS.has(itemId)) return null;
  if (
    LIMCO_ONLY_PRINT_IDS.has(itemId) &&
    !isLimcoOnlyPrintVisible(resolveExportPrintCompanyId(companyId))
  ) {
    return null;
  }

  const encoded = btoa(String(bookingId));
  const fileRef = fileNumber || "";

  switch (itemId) {
    case "booking_confirmation_freighted":
      return `/booking/print/${encoded}`;
    case "booking_confirmation_non_freighted":
      return `/booking/print-non-freighted/${encoded}`;
    case "dock_receipt":
      return `/dock-receipt/print/${encoded}`;
    case "pre_alert":
      return buildDocPrintUrl("/preAlertPrint", bookingId, fileRef);
    case "trucker_exporter_instructions":
      return buildDocPrintUrl("/workOrderPrint", bookingId, fileRef);
    case "mbl_master":
      return `/mblPrint?bookingId=${bookingId}&fileNumber=${encodeURIComponent(fileRef)}`;
    case "hbl_original_freighted":
      return buildHblPrintUrl(bookingId, fileRef, { watermarkType: "original" });
    case "hbl_original_non_freighted":
      return buildHblPrintUrl(bookingId, fileRef, {
        watermarkType: "original",
        hideCharges: true,
      });
    case "hbl_non_nego_freighted":
      return buildHblPrintUrl(bookingId, fileRef, { watermarkType: "non_nego" });
    case "hbl_non_nego_non_freighted":
      return buildHblPrintUrl(bookingId, fileRef, {
        watermarkType: "non_nego",
        hideCharges: true,
      });
    case "waybill_non_nego":
      return buildHblPrintUrl(bookingId, fileRef, { watermarkType: "waybill" });
    case "hbl_draft_proof":
      return buildHblPrintUrl(bookingId, fileRef, {
        watermarkType: "draft",
        hideCharges: true,
      });
    case "hbl_freighted_no_sign_watermark":
      return buildHblPrintUrl(bookingId, fileRef, {
        hideStamp: true,
        hideWatermark: true,
      });
    case "hbl_non_freighted_no_sign_watermark":
      return buildHblPrintUrl(bookingId, fileRef, {
        hideStamp: true,
        hideWatermark: true,
        hideCharges: true,
      });
    case "waybill":
      return buildHblPrintUrl(bookingId, fileRef, { watermarkType: "waybill" });
    case "certificate_of_origin":
      return buildDocPrintUrl("/certificateOfOriginPrint", bookingId, fileRef);
    case "commercial_invoice":
    case "customer_invoice":
      return buildDocPrintUrl("/customerInvoicePrint", bookingId, fileRef);
    case "packing_list":
      return buildDocPrintUrl("/packingListPrint", bookingId, fileRef);
    case "cover_page":
      return buildDocPrintUrl("/coverPagePrint", bookingId, fileRef);
    case "fcr":
      return buildDocPrintUrl("/fcrPrint", bookingId, fileRef);
    case "istruzioni_sdoganamento":
      return buildDocPrintUrl("/customsInstructionsPrint", bookingId, fileRef);
    case "ordine_di_trasporto":
      return buildDocPrintUrl("/transportOrderPrint", bookingId, fileRef);
    case "security_endorsement":
      return "/securityEndorsementPrint";
    case "screening_consent":
      return "/screeningConsentPrint";
    default:
      return null;
  }
}

/**
 * @param {{ items: { id: string, name: string, copies: number }[], blPrintOnLetterhead?: boolean, bookingId?: number|string, fileNumber?: string }} payload
 * @returns {number} count of windows opened
 */
export function openExportShipmentPrintDocuments({
  items,
  blPrintOnLetterhead = false,
  bookingId,
  fileNumber,
}) {
  if (!bookingId || Number(bookingId) <= 0) {
    toast.error("Booking ID missing.");
    return 0;
  }

  let delayMs = 0;
  let scheduled = 0;
  const openStaggered = (url) => {
    const finalUrl = appendBlLetterhead(url, blPrintOnLetterhead);
    scheduled += 1;
    window.setTimeout(() => {
      window.open(finalUrl, "_blank", "noopener,noreferrer");
    }, delayMs);
    delayMs += 350;
  };

  for (const item of items || []) {
    if (COMING_SOON_EXPORT_PRINT_IDS.has(item.id)) {
      toast.info(`${item.name} — coming soon.`);
      continue;
    }

    const path = resolveExportPrintPath(item.id, { bookingId, fileNumber });
    if (!path) {
      toast.error(`Unknown or unavailable document: ${item.name}`);
      continue;
    }

    const copies = Math.max(1, Number(item.copies) || 1);
    for (let c = 0; c < copies; c++) openStaggered(path);
  }

  return scheduled;
}
